// USB TSC label printer support via WebUSB + TSPL (TSC Printer Language).
// Chrome/Edge only — WebUSB is not implemented in Safari or Firefox.

const STORAGE_KEY = 'tscPrinterConfig';

export const DEFAULT_PRINTER_CONFIG = {
  widthMm: 50,
  heightMm: 30,
  gapMm: 2,
  darkness: 8,
  speed: 4,
  copies: 1,
};

export const isWebUSBSupported = () => typeof navigator !== 'undefined' && !!navigator.usb;

export const getPrinterConfig = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return { ...DEFAULT_PRINTER_CONFIG, ...stored };
  } catch {
    return DEFAULT_PRINTER_CONFIG;
  }
};

export const savePrinterConfig = (config) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
};

// Devices the user has already granted USB permission to (survives reload,
// no picker shown) — falls back to null if none was ever paired.
export const getAuthorizedPrinter = async () => {
  if (!isWebUSBSupported()) return null;
  const devices = await navigator.usb.getDevices();
  return devices[0] || null;
};

// Opens the browser's USB device picker. Must be called from a user gesture
// (click handler) — browsers block it otherwise.
export const requestPrinter = async () => {
  if (!isWebUSBSupported()) {
    throw new Error('WebUSB is not supported in this browser. Use Chrome or Edge.');
  }
  return navigator.usb.requestDevice({ filters: [] });
};

export const forgetPrinter = async (device) => {
  if (device?.forget) await device.forget();
};

const findPrinterInterface = (device) => {
  for (const config of device.configurations) {
    for (const iface of config.interfaces) {
      for (const alt of iface.alternates) {
        const outEndpoint = alt.endpoints.find((e) => e.direction === 'out');
        if (outEndpoint) {
          return {
            configurationValue: config.configurationValue,
            interfaceNumber: iface.interfaceNumber,
            endpointNumber: outEndpoint.endpointNumber,
          };
        }
      }
    }
  }
  return null;
};

export const sendTSPL = async (device, tsplCommand) => {
  const target = findPrinterInterface(device);
  if (!target) {
    throw new Error('No compatible USB print interface found on this device.');
  }

  const wasOpen = device.opened;
  if (!wasOpen) await device.open();
  try {
    if (!device.configuration || device.configuration.configurationValue !== target.configurationValue) {
      await device.selectConfiguration(target.configurationValue);
    }
    await device.claimInterface(target.interfaceNumber);
    try {
      await device.transferOut(target.endpointNumber, new TextEncoder().encode(tsplCommand));
    } finally {
      await device.releaseInterface(target.interfaceNumber);
    }
  } finally {
    if (!wasOpen) await device.close();
  }
};

const escapeTSPL = (value) => String(value ?? '').replace(/"/g, "'");

// Positions are in dots (203dpi printers, the common TSC default, use 8 dots/mm).
// widthMm/heightMm/gapMm describe the physical label stock; darkness (0-15) and
// speed (1-6 ips) may need recalibrating per printer/media — expose them so an
// admin can tune without a code change.
export const buildBarcodeLabelTSPL = (product, config = getPrinterConfig()) => {
  const { widthMm, heightMm, gapMm, darkness, speed, copies } = { ...DEFAULT_PRINTER_CONFIG, ...config };
  const name = escapeTSPL(product.name).slice(0, 40);
  // Must match the server's qrPayload format (productController.js): plain
  // barcode number only, no pipe-delimited uuid/signature. A longer payload
  // needs far more modules, making each bar thinner and slower to scan.
  // The server only enforces the anti-forgery signature check when a scan
  // actually includes uuid+signature, so a plain-barcode label still
  // verifies fine via barcode-number lookup.
  const barcodeValue = escapeTSPL(product.barcode);
  const batchLine = escapeTSPL(`Batch: ${product.batchNo || '-'}`);

  return [
    `SIZE ${widthMm} mm, ${heightMm} mm`,
    `GAP ${gapMm} mm, 0 mm`,
    'DIRECTION 1',
    'REFERENCE 0,0',
    `DENSITY ${darkness}`,
    `SPEED ${speed}`,
    'CLS',
    `TEXT 20,10,"3",0,1,1,"${name}"`,
    `BARCODE 20,40,"128",60,1,0,2,2,"${barcodeValue}"`,
    `TEXT 20,110,"2",0,1,1,"${batchLine}"`,
    `PRINT 1,${Math.max(1, Number(copies) || 1)}`,
    '',
  ].join('\r\n');
};

// High-level helper: reuses a previously authorized printer if there is one,
// otherwise prompts the picker, then sends the label.
export const printProductLabelViaUSB = async (product, config) => {
  if (!product?.barcode) {
    throw new Error('This product has no barcode to print.');
  }
  let device = await getAuthorizedPrinter();
  if (!device) device = await requestPrinter();
  const tspl = buildBarcodeLabelTSPL(product, config || getPrinterConfig());
  await sendTSPL(device, tspl);
  return device;
};

export const printTestLabelViaUSB = async (config) => {
  return printProductLabelViaUSB(
    { name: 'Aegis Verify Test Label', barcode: '000000000000', batchNo: 'TEST' },
    config
  );
};

// Bulk version — one USB device, N labels sent one after another (can't
// parallelize a single physical printer). Authorizes the printer once up
// front rather than once per label. onProgress is optional, called after
// each label so the caller can show "12 / 100 printed" during a large batch.
export const printProductLabelsViaUSB = async (products, config, onProgress) => {
  const printable = products.filter((p) => p?.barcode);
  if (!printable.length) {
    throw new Error('None of the selected products have a barcode to print.');
  }
  let device = await getAuthorizedPrinter();
  if (!device) device = await requestPrinter();

  const resolvedConfig = config || getPrinterConfig();
  const failed = [];
  for (let i = 0; i < printable.length; i++) {
    const product = printable[i];
    try {
      const tspl = buildBarcodeLabelTSPL(product, resolvedConfig);
      await sendTSPL(device, tspl);
    } catch (err) {
      failed.push({ product, error: err.message });
    }
    onProgress?.(i + 1, printable.length);
  }
  return { device, total: printable.length, failed };
};
